import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { REACT_APP_API_URL } from "../../../../../env";
import axios from "axios";
import "bootstrap-icons/font/bootstrap-icons.css";

const columns = [
  {
    name: "Property",
    selector: (row) => row.PropertyName,
    sortable: true,
    minWidth: "180px",
  },
  {
    name: "Booking Ref No",
    selector: (row) => row.BookingRefNo,
    sortable: true,
    minWidth: "180px",
  },
  {
    name: "Customer Name",
    selector: (row) => row.CustomerName,
    sortable: true,
    minWidth: "160px",
  },
  {
    name: "Booking Date",
    selector: (row) => row.IssueDate,
    sortable: true,
    minWidth: "160px",
  },
  {
    name: "Cancel Date",
    selector: (row) => row.BillDate,
    sortable: true,
    minWidth: "160px",
  },
  {
    name: "Check-in",
    selector: (row) => row.CheckinDate,
    sortable: true,
    minWidth: "160px",
  },
  {
    name: "Check-out",
    selector: (row) => row.CheckOutDate,
    sortable: true,
    minWidth: "160px",
  },
  {
    name: "Cancel Amount",
    selector: (row) => row.Discount,
    sortable: true,
    right: true,
    minWidth: "160px",
  },
  {
    name: "Tax",
    selector: (row) => row.TaxAmount,
    sortable: true,
    right: true,
    minWidth: "160px",
  },
  {
    name: "Refund Amount",
    selector: (row) => row.Amount,
    sortable: true,
    right: true,
    minWidth: "160px",
  },
  {
    name: "Total",
    selector: (row) => row.TotalAmount,
    sortable: true,
    right: true,
    minWidth: "160px",
  },
];


const customStyles = {
  rows: {
    style: {
      minHeight: "45px",
      borderBottom: "1px solid #dee2e6",
    },
  },
  headCells: {
    style: {
      fontWeight: "bold",
      fontSize: "14px",
      border: "1px solid #dee2e6",
      backgroundColor: "#f8f9fa",
      position: "sticky",
      top: 0,
      zIndex: 1,
      whiteSpace: "nowrap",
      overflow: "visible",
      textOverflow: "clip",
    },
  },
  headRow: {
    style: {
      position: "sticky",
      top: 0,
      zIndex: 2,
      backgroundColor: "#f8f9fa",
    },
  },
  cells: {
    style: {
      border: "1px solid #dee2e6",
    },
  },
};

const FullCancelList = () => {
  const [data, setData] = useState([]);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState(null);

  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [searchBookingRef, setSearchBookingRef] = useState("");
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    fetch(`${REACT_APP_API_URL}api/dashboard/admin/history/fully-cancelled`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch full cancelled bookings");
        return res.json();
      })
      .then((result) => {
        if (result.success) {
          setData(result.data);
        } else {
          setError("No data received");
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setPending(false));
  }, []);

  const handleCSVExport = () => {
    const csvRows = [];

    // Create the header row
    csvRows.push(columns.map((col) => `"${col.name}"`).join(","));

    // Add data rows
    filteredData.forEach((row) => {
      const rowData = columns.map(
        (col) => `"${col.selector ? col.selector(row) : ""}"`
      );
      csvRows.push(rowData.join(","));
    });

    const csvContent = csvRows.join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "CancelledBookings.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Date change handlers with validation
  const handleFromDateChange = (e) => {
    const value = e.target.value;
    setFilterFrom(value);
    if (filterTo && value && new Date(value) > new Date(filterTo)) {
      setDateError("From date cannot be after To date.");
    } else {
      setDateError("");
    }
  };

  const handleToDateChange = (e) => {
    const value = e.target.value;
    setFilterTo(value);
    if (filterFrom && value && new Date(filterFrom) > new Date(value)) {
      setDateError("From date cannot be after To date.");
    } else {
      setDateError("");
    }
  };

  const filteredData = useMemo(() => {
    if (dateError) return [];
    return data.filter((item) => {
      const checkinDate = new Date(item.CheckinDate);
      const fromDate = filterFrom ? new Date(filterFrom) : null;
      const toDate = filterTo ? new Date(filterTo) : null;

      const matchesFrom = fromDate ? checkinDate >= fromDate : true;
      const matchesTo = toDate ? checkinDate <= toDate : true;
      const matchesSearch = item.BookingRefNo.toLowerCase().includes(
        searchBookingRef.toLowerCase()
      );

      return matchesFrom && matchesTo && matchesSearch;
    });
  }, [data, filterFrom, filterTo, searchBookingRef, dateError]);

  const handleExcelExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "CancelledBookings");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "CancelledBookings.xlsx");
  };

  const handlePdfExport = () => {
    const doc = new jsPDF();

    const head = [columns.map((col) => col.name)];
    const body = filteredData.map((row) =>
      columns.map((col) =>
        typeof col.selector === "function" ? col.selector(row) : ""
      )
    );
    autoTable(doc, {
      head,
      body,
      styles: { fontSize: 8 },
      margin: { top: 10 },
    });

    doc.save("CancelledBookings.pdf");
  };
  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row g-3 mb-3">
        <div className="col-12 d-flex flex-wrap justify-content-between align-items-center">
          <h2 className="mb-0">Fully Cancelled Booking List</h2>
        </div>
      </div>

      {dateError && (
        <div className="col-12">
          <div className="alert alert-danger py-2">{dateError}</div>
        </div>
      )}

      <div className="row g-3">
        <div className="col-12">
          <div className="card shadow-sm rounded-1">
            <div className="card-body">
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="d-flex flex-wrap gap-3 align-items-end mt-5 mb-5 p-3 border rounded">
                {/* From Date */}
                <div className="d-flex flex-column">
                  <label className="form-label mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filterFrom}
                    onChange={handleFromDateChange}
                    max={filterTo || undefined}
                  />
                </div>

                {/* To Date */}
                <div className="d-flex flex-column">
                  <label className="form-label mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filterTo}
                    onChange={handleToDateChange}
                    min={filterFrom || undefined}
                  />
                </div>

                {/* Search Booking Ref */}
                <div className="d-flex flex-column">
                  <label className="form-label mb-1">Booking Ref</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search Booking Ref"
                    value={searchBookingRef}
                    onChange={(e) => setSearchBookingRef(e.target.value)}
                  />
                </div>

                {/* Export Buttons */}
                <div className="d-flex flex-column">
                  <label className="form-label mb-1">Export</label>
                  <div className="d-flex gap-2">
                    <CSVLink
                      data={filteredData}
                      filename="CancelledBookings.csv"
                      className="btn btn-sm btn-outline-primary"
                      title="Export CSV"
                    >
                      <i className="bi bi-file-earmark"></i>
                    </CSVLink>

                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={handleExcelExport}
                      title="Export Excel"
                    >
                      <i className="bi bi-file-earmark-excel"></i>
                    </button>

                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={handlePdfExport}
                      title="Export PDF"
                    >
                      <i className="bi bi-filetype-pdf"></i>
                    </button>
                  </div>
                </div>
              </div>

              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                paginationPerPage={100}
                paginationRowsPerPageOptions={[50, 100, 500]}
                responsive
                striped
                highlightOnHover
                progressPending={pending}
                customStyles={customStyles}
                noDataComponent="No fully cancelled bookings found."
                fixedHeader
                fixedHeaderScrollHeight="500px"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullCancelList;
